import type { z } from 'zod';
import { apiError } from '@gg/shared';
import { config } from './config';

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Typed fetch wrapper. Validates the response against a Zod schema so a
 * backend drift fails loudly in dev rather than rendering `undefined`.
 */
export async function api<T>(
  path: string,
  opts: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    schema?: z.ZodType<T>;
  } = {},
): Promise<T> {
  const res = await fetch(`${config.apiUrl}${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers: opts.body ? { 'content-type': 'application/json' } : undefined,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 204 || res.status === 202) return undefined as T;

  const json: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const parsed = apiError.safeParse(json);
    if (parsed.success) {
      const e = parsed.data.error;
      throw new ApiRequestError(res.status, e.code, e.message, e.details);
    }
    throw new ApiRequestError(res.status, 'http_error', res.statusText);
  }
  return opts.schema ? opts.schema.parse(json) : (json as T);
}
